import { useQuery } from '@apollo/client';
import type { Op } from 'quill/core';
import React, { useLayoutEffect, useMemo } from 'react';
import HomeActivities from './HomeActivities';
import useViewedPostsColumn from './useViewedPostsColumn';
import PageLayout from '../app/layout/PageLayout';
import { useAppDispatch } from '../app/redux/useAppSelector';
import SideColumnLayout from '../components/SideColumnLayout';
import { UIActions } from '../ui/uiSlice';
import ReferralModal from '../user/ReferralModal';
import OrganizationSummary from './organizationSummary/OrganizationSummary';
import { gql } from '../graphql/generated/gql';
import { openPricingModal } from '../utils/url/pricingPath';

const ORGANIZATION_SUMMARY_QUERY = gql(/** @complexity 2 */ `
  query organizationSummary {
    session: currentSession {
      organization {
        id
        summary
      }
    }
  }
`);

type HomeModal = 'pricing' | 'referral';

interface HomeViewProps {
  openModal?: HomeModal;
}

const useModal = (modal?: HomeModal) => {
  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (modal === 'pricing') {
      openPricingModal(dispatch);
    } else if (modal === 'referral') {
      dispatch(UIActions.openModal(ReferralModal));
    } else {
      return undefined;
    }

    return () => {
      dispatch(UIActions.closeModal());
    };
  }, [dispatch, modal]);
};

const HomeView = ({ openModal }: HomeViewProps) => {
  const { data, loading } = useQuery(ORGANIZATION_SUMMARY_QUERY);
  const organization = data?.session?.organization;
  const summary = useMemo(() => {
    if (organization?.summary) {
      return JSON.parse(organization.summary) as Op[];
    }
    return null;
  }, [organization]);
  const main = useViewedPostsColumn();
  useModal(openModal);

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <SideColumnLayout
        aboveMain={<OrganizationSummary summary={summary} />}
        main={loading && !summary ? undefined : main}
        sideColumn={{
          title: 'Activity',
          children: <HomeActivities />,
        }}
      />
    </PageLayout.Content>
  );
};

export default HomeView;
